// Navigation Items Management Tab
// Date: November 26, 2025

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { contentManagementService, PublicNavigationItem } from '@/services/contentManagementService';
import { toast } from 'react-hot-toast';
import { Switch } from '@/components/ui/Switch';

export const NavigationItemsTab: React.FC = () => {
    const [items, setItems] = useState<PublicNavigationItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState<string | null>(null);

    useEffect(() => {
        loadItems();
    }, []);

    const loadItems = async () => {
        try {
            setLoading(true);
            const data = await contentManagementService.getAllNavigationItems();
            setItems(data.sort((a, b) => a.display_order - b.display_order));
        } catch (error: any) {
            console.error('Error loading navigation items:', error);
            toast.error(error.response?.data?.message || 'Failed to load navigation items');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (item: PublicNavigationItem) => {
        try {
            setToggling(item.id);
            await contentManagementService.toggleNavigationItem(item.id, !item.is_active);
            toast.success(`Navigation item ${!item.is_active ? 'enabled' : 'disabled'} successfully`);
            await loadItems();
        } catch (error: any) {
            console.error('Error toggling navigation item:', error);
            toast.error(error.response?.data?.message || 'Failed to toggle navigation item');
        } finally {
            setToggling(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-accent-bg)]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="text-sm text-[var(--color-secondary-text)] mb-4">
                Control which navigation menu items appear on the public website. Toggle items on or off to show or hide them in the navigation menu.
            </div>

            <div className="grid gap-4">
                {items.map((item) => (
                    <Card key={item.id} className={!item.is_active ? 'opacity-60' : ''}>
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                        <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">
                                            {item.label}
                                        </h3>
                                        {!item.is_active && (
                                            <span className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded">
                                                Hidden
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-sm text-[var(--color-secondary-text)] mt-1">
                                        Path: <code className="bg-[var(--color-secondary-bg)] px-1 rounded">{item.path}</code>
                                    </p>
                                    <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                                        Key: <code className="bg-[var(--color-secondary-bg)] px-1 rounded">{item.item_key}</code>
                                    </p>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-[var(--color-surface-text)]">
                                            {item.is_active ? 'Visible' : 'Hidden'}
                                        </span>
                                        <Switch
                                            checked={item.is_active}
                                            onCheckedChange={() => handleToggle(item)}
                                            disabled={toggling === item.id}
                                        />
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        </div>
    );
};

